import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sprout, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setSubmitting(true);
    try {
      await login(username, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid username or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#F7F7F5]">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative overflow-hidden bg-stone-900 text-white grain">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1618763604262-6cbefa2a7b8e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/80 via-stone-900/40 to-emerald-950/60" />
        <div className="relative z-10 flex flex-col justify-between p-10 lg:p-14 w-full">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/90 text-white">
              <Sprout size={20} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">Hydro Stack</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-300">
                Made by Sukesh Barua
              </div>
            </div>
          </div>
          <div className="space-y-5 max-w-md fade-in-up">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
              Hour-precise growth
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-light leading-[1.05] tracking-tight">
              Seven days. <br />
              <span className="text-emerald-300/90">Seven stages.</span> <br />
              One clean dashboard.
            </h1>
            <p className="text-stone-300 text-sm leading-relaxed">
              Track every order from soaking to harvest with live-ticking timers,
              auto-computed grain & tray requirements, and a 7-segment progress bar
              that always shows the day at a glance.
            </p>
          </div>
          <div className="text-[11px] text-stone-400 tracking-wide">
            v1 · single-user workspace
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm space-y-7 fade-in-up"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-emerald-600 text-white">
              <Sprout size={18} />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-stone-900">Hydro Stack</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
                Made by Sukesh Barua
              </div>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2">
              Secure entry
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-stone-900">
              Welcome back.
            </h2>
            <p className="text-sm text-stone-500 mt-2">
              Sign in with your admin credentials.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-stone-600" htmlFor="username">
                Login ID
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Login ID"
                className="h-11 bg-white border-stone-200"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-stone-600" htmlFor="password">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pr-10 h-11 bg-white border-stone-200"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || !username || !password}
            data-testid="login-submit-btn"
            className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium tracking-wide"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-[11px] text-stone-400 leading-relaxed">
            Single-user workspace. You can change your password from the dashboard once signed in.
          </div>
        </form>
      </div>
    </div>
  );
}
